import {
  type FC,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { cn } from '@/shared/react';

type CoinTab = {
  key: string;
  label: string;
  ref: RefObject<HTMLElement>;
};

type CoinTabsProps = {
  tabs: CoinTab[];
  activeTab?: string;
  onTabClick: (tab: CoinTab) => void;
};

export const CoinTabs: FC<CoinTabsProps> = ({
  tabs,
  activeTab,
  onTabClick,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [isFloating, setIsFloating] = useState(false);

  const handleScroll = useCallback(() => {
    const top = rootRef.current?.getBoundingClientRect().top ?? 1;
    setIsFloating(top <= 0);
  }, []);

  useEffect(() => {
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const renderTabs = () => (
    <div className="max-w-desktop mx-auto flex gap-4 overflow-auto scrollbar-none px-4">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          onClick={() => onTabClick(tab)}
          // to title case using tailwind css
          className={cn(
            'py-4 text-xs tracking-wide border-b-2 transition-colors cursor-pointer capitalize',
            activeTab === tab.key
              ? 'text-content-primary border-b-background-brand'
              : 'text-content-tertiary border-transparent',
          )}
        >
          {tab.label.replace(/_/g, ' ')}
        </button>
      ))}
    </div>
  );

  return (
    <>
      <div
        ref={rootRef}
        className={cn(
          'sticky top-0 z-20 bg-background-primary border-b border-border-disabled',
          isFloating && 'invisible',
        )}
      >
        {renderTabs()}
      </div>
      {isFloating && (
        <div className="fixed top-0 left-0 right-0 z-40 bg-background-primary border-b border-border-disabled px-3">
          {renderTabs()}
        </div>
      )}
    </>
  );
};
